import type Store from '@ember-data/store';
import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import formatDate from 'ember-intl/helpers/format-date';
import WrButton from 'ember-wiser/components/button';
import WrForm, {
  type WrFormComponentSignature as WrFormSig
} from 'ember-wiser/components/form';
import { jeCreator } from 'parog-web/helpers/je-creator';
import type Patient from 'parog-web/models/patient';
import type TreatmentPrescriptionChangedJournalEvent from 'parog-web/models/treatment-prescription-changed-journal-event';
import { sortBy } from 'parog-web/utils/sort';
import emberSwal from 'ui-kit/utils/ember-swal';
import dayjsFromNow from 'parog-web/helpers/dayjs-from-now';
import DosageColumn from './-private/dosage-column';
import Dropdown from './-private/dropdown';
import MedicationColumns from './-private/medication-columns';
import StoppingColumn from './-private/stopping-column';
import type { TreatmentFormChangeset } from './treatment-form-modal';

interface Signature {
  Args: {
    patient: Patient;
    changeset: TreatmentFormChangeset;
    journalEvent?: TreatmentPrescriptionChangedJournalEvent;
    onSubmit: () => void;
    onCancel: () => void;
  };
}

export interface ProcessedMedications {
  key: string;
  value: string;
}

type Form = WrFormSig<TreatmentFormChangeset, unknown>['Blocks']['default'][0];

interface RowSig {
  Args: {
    prescription: TreatmentFormChangeset['data']['prescriptions'][number];
    form: Form;
    onDelete: (index: number) => void;
    medicationsGroupedByAtc: TreatementFormComponent['medicationsGroupedByAtc'];
    subSetMedication: Set<medsInterface>;
  };
}

export interface medsInterface {
  brand?: string;
  administrationRoute?: string;
  moleculeLabel?: string;
  dose?: number;
  unit?: string;
  name?: string;
}

/** Renders a HTMLTableRowElement with the prescription information.
 * It also handles the dropdown display and the deletion of a prescription.
 * */
class PrescriptionRow extends Component<RowSig> {
  /** Returns @form.data typed as NonNullable */
  get changeset() {
    return this.args.form.data as NonNullable<typeof this.args.form.data>;
  }

  get currentMedication(): medsInterface {
    const existingMedication =
      this.args.subSetMedication[
        this.changeset[this.prefix('medication')] as string
      ];
    return (
      existingMedication || { name: this.changeset[this.prefix('medication')] }
    );
  }

  get isCurrentMedicationStopped(): boolean {
    return !!this.changeset[this.prefix('endDate')];
  }

  /** Factorize the prefix construction for the prescription properties in the changeset */
  prefix = (property: string) => {
    const sep = '_';
    return `prescriptions${sep}${this.args.prescription.index}${sep}${property}`;
  };

  <template>
    <tr data-test-prescription={{@prescription.index}}>
      <td class='align-top'>
        <@form.Input @name={{this.prefix 'startDate'}} type='date' />
      </td>

      <MedicationColumns
        @changeset={{this.changeset}}
        @prefix={{this.prefix}}
        @medicationsGroupedByAtc={{@medicationsGroupedByAtc}}
        @currentMedication={{this.currentMedication}}
        @isCurrentMedicationStopped={{this.isCurrentMedicationStopped}}
      />

      <td class='align-top'>
        <DosageColumn
          @changeset={{this.changeset}}
          @form={{@form}}
          @medication={{this.currentMedication}}
          @prefix={{this.prefix}}
        />
      </td>

      <td class='align-top'>
        <StoppingColumn
          @changeset={{this.changeset}}
          @form={{@form}}
          @prefix={{this.prefix}}
        />
      </td>

      <td class='align-top'><WrButton
          @variant='link'
          @icon='Trash'
          {{on 'click' (fn @onDelete @prescription.index)}}
          data-test-remove-prescription={{@prescription.index}}
        /></td>
    </tr>
  </template>
}

export default class TreatementFormComponent extends Component<Signature> {
  @service declare store: Store;

  get medicationsGroupedByAtc(): Map<string, ProcessedMedications[]> {
    // Group medication by ATC code
    return this.#medications
      .slice()
      .sort(sortBy('medicationMolecule.isGroupLeader', 'desc'))
      .reduce((acc: Map<string, ProcessedMedications[]>, medication) => {
        const atc = medication.medicationMolecule.atc;
        // If the molecule is not already in the accumulator, add it
        if (!acc.has(atc)) {
          // Set the accumulator value with the current medication
          acc.set(atc, [
            { key: medication.cisCode, value: medication.longDescription }
          ]);
        } else {
          // else, push the current medication in its value
          acc.get(atc)?.push({
            key: medication.cisCode,
            value: medication.longDescription
          });
        }

        return acc;
      }, new Map());
  }

  // TODO A améliorer.
  get orderedPrescriptions() {
    const prescriptions = this.args.changeset.get(
      'prescriptions'
    ) as (typeof this.args.changeset.data)['prescriptions'];

    const items = prescriptions ?? [];

    return [...items].sort((a, b) => {
      const hasEndA = !!a.endDate;
      const hasEndB = !!b.endDate;

      if (hasEndA !== hasEndB) {
        return hasEndA ? 1 : -1;
      }

      const hasStartA = !!a.startDate;
      const hasStartB = !!b.startDate;

      if (hasStartA !== hasStartB) {
        return hasStartA ? -1 : 1;
      }

      if (!hasStartA && !hasStartB) {
        return 0;
      }

      return (
        new Date(b.startDate!).getTime() - new Date(a.startDate!).getTime()
      );
    });
  }

  /**
   * returns a set of medication with a subset of properties
   **/
  get subSetMedication(): Set<medsInterface> {
    const meds = new Set<medsInterface>();
    this.#medications.forEach(medication => {
      const brand = medication.brand;
      const administrationRoute = medication.administrationRoute;
      const moleculeLabel = medication.medicationMolecule.label;
      const dose = medication.dose;
      const unit = medication.unit;
      meds[medication.cisCode as string] = {
        dose,
        unit,
        brand,
        moleculeLabel,
        administrationRoute
      };
    });
    return meds;
  }

  get therapeuticalClassItems() {
    return this.store.peekAll('medication-therapeutical-group').slice();
  }

  get #medications() {
    return this.store.peekAll('medication-specialty').slice();
  }

  addNewPrescription = () => {
    const prescriptions = this.args.changeset.get('prescriptions') || [];

    const index = prescriptions?.length || 0;
    const newPrescription = {
      startDate: new Date(),
      index: index,
      isDeleted: false,
      medication: null,
      doseQuantity: null,
      timing: null,
      comment: null,
      endDate: null,
      stoppingReason: null,
      stoppingComment: null,
      isNew: true
    };
    const newPrescriptions = [...prescriptions, newPrescription];

    this.args.changeset.set('prescriptions', newPrescriptions);
  };

  dropPrescription = async (index: number) => {
    const result = await emberSwal.fire({
      title: 'Confirmer la suppression',
      text: 'Cette opération ne peut pas être annulée.',
      icon: 'warning',
      cancelButtonText: 'Annuler',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer'
    });
    if (result.value) {
      const prescriptions = this.args.changeset.get('prescriptions');
      const updatedPrescriptions = prescriptions.map(p => {
        if (p.index == index) {
          return {
            ...p,
            isDeleted: true
          };
        }
        return p;
      });

      this.args.changeset.set('prescriptions', updatedPrescriptions);
    }
  };

  <template>
    <div class='m-4 flex max-w-[85vw] justify-between'>
      {{#if @journalEvent}}
        <div class='bg-purple-light text-purple-medium rounded-md p-2'>
          <i class='info circle icon' />
          Informations renseignées par
          <span class='font-bold'>{{jeCreator (array @journalEvent)}}</span>
          le
          <span class='font-bold'>{{formatDate
              @journalEvent.updatedAt
              format='DD MMMM YYYY'
            }}
            (
            {{dayjsFromNow @journalEvent.updatedAt}}
            )
          </span>
        </div>
      {{else}}
        <span></span>
      {{/if}}
      <WrButton {{on 'click' this.addNewPrescription}}>+ Ajouter un traitement</WrButton>
    </div>

    <WrForm
      @data={{@changeset}}
      @onSubmit={{@onSubmit}}
      data-test-treatments-table
      class='max-w-[85vw] overflow-x-auto pb-8'
      as |form|
    >
      <table class='ui unstackable table'>
        <thead>
          <tr>
            <th class='!bg-grey-light'>Début</th>
            <th class='!bg-grey-light'>NC</th>
            <th class='!bg-grey-light'>DCI</th>
            <th class='!bg-grey-light'>Dose</th>
            <th class='!bg-grey-light'>Administration</th>
            <th class='!bg-grey-light'></th>
            <th class='!bg-grey-light'>Posologie</th>
            <th class='!bg-grey-light'>Arrêt</th>
            <th class='!bg-grey-light'></th>
          </tr>
        </thead>

        <tbody>
          {{#each this.orderedPrescriptions as |prescription|}}
            {{#unless prescription.isDeleted}}
              <PrescriptionRow
                @prescription={{prescription}}
                @form={{form}}
                @onDelete={{this.dropPrescription}}
                @medicationsGroupedByAtc={{this.medicationsGroupedByAtc}}
                @subSetMedication={{this.subSetMedication}}
              />
            {{/unless}}
          {{/each}}
        </tbody>
      </table>

      {{#if @patient.hasDiabetoPathology}}
        <Dropdown
          @changeset={{@changeset}}
          @propertyName='recommendedTherapeuticalClasses'
          @labelName='Classes thérapeutiques recommandées'
          @multipleSelection={{true}}
          as |dropdown|
        >
          {{#each this.therapeuticalClassItems as |item|}}
            <dropdown.Item @key={{item.id}}>{{item.id}}</dropdown.Item>
          {{/each}}
        </Dropdown>
        <Dropdown
          @changeset={{@changeset}}
          @propertyName='notRecommendedTherapeuticalClasses'
          @labelName='Classes thérapeutiques déconseillées'
          @multipleSelection={{true}}
          as |dropdown|
        >
          {{#each this.therapeuticalClassItems as |item|}}
            <dropdown.Item @key={{item.id}}>{{item.id}}</dropdown.Item>
          {{/each}}
        </Dropdown>
      {{/if}}

      <div class='flex justify-end gap-2'>
        <form.CancelButton {{on 'click' @onCancel}}>Fermer</form.CancelButton>
        <form.SubmitButton>
          Sauvegarder
        </form.SubmitButton>
      </div>
    </WrForm>
  </template>
}
